/* eslint-disable no-plusplus */
import React, { useCallback, useMemo, useRef, useState } from "react";

import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { Color } from "../../../../../../../../../components/Form/Button";
import { BadgeImportFileStatus } from "../../../../../../../../../components/Generic/BadgeImportFileStatus";
import { NotificationToast } from "../../../../../../../../../components/Generic/Toast/NotificationToast";
import { capitalizeFirstLetterAllWorlds } from "../../../../../../../../../util/capitalizeFirstLetter";
import { paginate } from "../../../../../../../../../util/paginate";
import { ProjectEmptySection } from "../EmptySection";
import { useActionProjectFiles } from "./hooks/useAction";
import { FileInput, PlusIcon, StyledButton } from "./styles";

interface ProjectFilesProps {
  projectId: number;
  isView: boolean;
}

export const ProjectFiles = ({ projectId, isView }: ProjectFilesProps) => {
  const { t: translate } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);

  const { variables, uploadCSV, setCurrentPage } = useActionProjectFiles({ projectId });
  const { filesList, isLoadingProjectFiles, total, page } = variables;

  const [pageSize, setPageSize] = useState<number>(5);

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const localData = useMemo(() => {
    return paginate(filesList, page > 0 ? page - 1 : 0, pageSize);
  }, [filesList, page, pageSize]);

  const columns: Column[] = [
    {
      name: "file_name",
      title: translate("fileName"),
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "createdAt",
      title: translate("dateUploaded"),
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "records_imported",
      title: translate(`# ${translate("ofRows")}`),
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) =>
        status && <BadgeImportFileStatus value={capitalizeFirstLetterAllWorlds(status)} />,
      hideSearch: true,
      hideSort: true,
    },
  ];

  const onChangeImport = async (files: any) => {
    if (files.length > 0) {
      const data = new FormData();

      data.append("file", files[0]);

      NotificationToast.info(`${translate("theFileWasSentToImport")}!`);

      uploadCSV({ file: data });
    } else {
      setTimeout(() => {
        NotificationToast.warning(translate("pleaseSelectAFile"));
      }, 500);
    }
  };

  const renderAddFile = useCallback(() => {
    if (isView) return null;

    return (
      <div>
        <StyledButton
          customColor={Color.SECONDARY}
          onClick={(e) => {
            e.preventDefault();
            ref.current?.click();
          }}
        >
          <PlusIcon color="#ffffff" size={18} />
          {translate("addFile")}
        </StyledButton>
      </div>
    );
  }, []);

  const generalActions: GeneralActions = {
    orderBy: {
      right: ["handleAddFile"],
    },
    handleAddFile: renderAddFile(),
  };

  const renderContent = () => {
    if (!isLoadingProjectFiles && !variables?.filesList?.length) {
      return (
        <ProjectEmptySection
          buttonLabel={translate("uploadAFile")}
          descriptionScreen={`${translate("youWillBeAbleToSeeAllThe")} ${translate(
            "files"
          ).toLowerCase()} ${translate("detailsHereWhenYouUploadAtLeastOne")}`}
          onAdd={() => {
            ref.current?.click();
          }}
        />
      );
    }

    return (
      <ShipeeziNewGrid
        data={localData ?? []}
        emptyData={translate("noInformationToBeShown")}
        totalItems={total}
        columns={columns}
        loadingData={isLoadingProjectFiles}
        currentPage={page}
        pageSize={pageSize}
        pageSizeList={[5, 20, 40]}
        onChangePageSize={handlePageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        generalActions={generalActions}
        positionActionsColumn="right"
      />
    );
  };

  return (
    <div>
      <FileInput
        id="importCsv"
        type="file"
        accept=".xlsx,.csv"
        style={{ display: "none" }}
        ref={ref}
        onChange={(event) => {
          onChangeImport(event.target.files);
          event.target.value = "";
        }}
      />
      {renderContent()}
    </div>
  );
};
